import { useMemo } from "react";
import { useNavigate, type NavigateOptions } from "react-router-dom";
import { prefersReducedMotion } from "./motion";

// Step-to-step navigation inside the auth flow runs as a View Transition: the
// logo glides to its new spot, the title cross-fades and the form slides in the
// direction of travel (see AuthLayout.css). BrowserRouter has no built-in view
// transition support — it commits route changes inside startTransition — so the
// transition is held open until the next step's AuthLayout reports it mounted.

export type StepDirection = "forward" | "back";

// Longest we hold the frozen frame if the route never changes (e.g. "back" with
// no history entry) or the target page is not an auth step.
const RENDER_TIMEOUT_MS = 400;

let stepRendered: (() => void) | null = null;

/** Called by AuthLayout on mount: the next step is in the DOM. */
export function authStepRendered() {
  stepRendered?.();
  stepRendered = null;
}

function transitionStep(navigate: () => void, direction: StepDirection) {
  if (!("startViewTransition" in document) || prefersReducedMotion()) {
    navigate();
    return;
  }
  const root = document.documentElement;
  root.dataset.authDir = direction;
  const transition = document.startViewTransition(
    () =>
      new Promise<void>((resolve) => {
        stepRendered = resolve;
        window.setTimeout(resolve, RENDER_TIMEOUT_MS);
        navigate();
      }),
  );
  void transition.finished.finally(() => {
    delete root.dataset.authDir;
  });
}

/** Navigation between auth steps. `go` moves forward (or back, when asked) to
 *  a path, `back` pops history — both animated where the browser supports it. */
export function useAuthNav() {
  const nav = useNavigate();
  return useMemo(
    () => ({
      go: (to: string, direction: StepDirection = "forward", options?: NavigateOptions) =>
        transitionStep(() => nav(to, options), direction),
      back: () => transitionStep(() => nav(-1), "back"),
    }),
    [nav],
  );
}
